/**
 * Data layer for `real-time-roi-measurement-per-creator`. Thin typed fetch wrappers over /api/v1/real-time-roi-measurement-per-creator.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface RealTimeRoiMeasurementPerCreatorRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListRealTimeRoiMeasurementPerCreatorResponse {
	count: number;
	items: RealTimeRoiMeasurementPerCreatorRow[];
}

export interface CreateRealTimeRoiMeasurementPerCreatorBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/real-time-roi-measurement-per-creator";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRealTimeRoiMeasurementPerCreatorResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRealTimeRoiMeasurementPerCreatorResponse>(res);
}

export async function create(body: CreateRealTimeRoiMeasurementPerCreatorBody): Promise<RealTimeRoiMeasurementPerCreatorRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<RealTimeRoiMeasurementPerCreatorRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const realTimeRoiMeasurementPerCreatorQueryKeys = {
	all: ["real-time-roi-measurement-per-creator"] as const,
	list: () => [...realTimeRoiMeasurementPerCreatorQueryKeys.all] as const,
	detail: (id: string) => [...realTimeRoiMeasurementPerCreatorQueryKeys.all, id] as const,
};
